<template>
  <div class="payment-link-card" :class="{ 'card-active': isActive, 'card-paid': isPaid }">
    <div class="card-header">
      <PaymentLinksStatusBadge :status="paymentLink.status" />
      <span class="card-date">{{ formatDate(paymentLink.created_at) }}</span>
    </div>

    <div class="card-body">
      <div class="card-amount">{{ formatCurrency((paymentLink.amount ?? 0) / 100) }}</div>

      <div class="card-details">
        <div class="detail-row" v-if="(paymentLink.installments ?? 0) > 1">
          <span class="detail-label">{{ t('Parcelas') }}:</span>
          <span class="detail-value">{{ paymentLink.installments }}x</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">{{ t('Forma') }}:</span>
          <span class="detail-value">{{ getPaymentMethodLabel(paymentLink.payment_method) }}</span>
        </div>
        <div class="detail-row" v-if="paymentLink.due_date">
          <span class="detail-label">{{ t('Vencimento') }}:</span>
          <span class="detail-value">{{ formatDate(paymentLink.due_date) }}</span>
        </div>
      </div>
    </div>

    <div class="card-actions" v-if="showActions">
      <Button
        v-if="isActive"
        data-testid="payment-links-card-open-link-button"
        icon="pi pi-external-link"
        class="p-button-text p-button-sm"
        v-tooltip.top="t('Abrir link')"
        @click="handleOpenLink"
      />
      <Button
        v-if="isActive"
        data-testid="payment-links-card-copy-link-button"
        icon="pi pi-copy"
        class="p-button-text p-button-sm"
        v-tooltip.top="t('Copiar link')"
        @click="handleCopyLink"
      />
      <Button
        v-if="isActive"
        data-testid="payment-links-card-whatsapp-button"
        icon="pi pi-whatsapp"
        class="p-button-text p-button-sm p-button-success"
        v-tooltip.top="t('Enviar via WhatsApp')"
        @click="handleSendWhatsApp"
      />
      <Button
        data-testid="payment-links-card-view-details-button"
        icon="pi pi-eye"
        class="p-button-text p-button-sm"
        v-tooltip.top="t('Ver detalhes')"
        @click="handleViewDetails"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { STATUS } from '../../composables/usePaymentLinks';
import { useDateFormatter } from '../../composables/useDateFormatter';
import PaymentLinksStatusBadge from './PaymentLinksStatusBadge.vue';

interface PaymentLink {
  id?: number | string;
  status?: string;
  amount?: number;
  installments?: number;
  payment_method?: string;
  due_date?: string | null;
  created_at?: string;
  url?: string;
  [key: string]: unknown;
}

const props = withDefaults(
  defineProps<{
    paymentLink: PaymentLink;
    showActions?: boolean;
  }>(),
  {
    showActions: true,
  }
);

const emit = defineEmits<{
  (e: 'copy', paymentLink: PaymentLink): void;
  (e: 'send-whatsapp', paymentLink: PaymentLink): void;
  (e: 'view-details', paymentLink: PaymentLink): void;
  (e: 'open-link', paymentLink: PaymentLink): void;
}>();

const { t } = useI18n();
const { formatDate: formatDateLocale } = useDateFormatter();

// Computed
const isActive = computed<boolean>(() => props.paymentLink?.status === STATUS.ACTIVE);
const isPaid = computed<boolean>(() => props.paymentLink?.status === STATUS.PAID);

// Methods
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return formatDateLocale(date);
};

const getPaymentMethodLabel = (method?: string): string => {
  const labels: Record<string, string> = {
    any: t('Qualquer'),
    pix: 'PIX',
    boleto: 'Boleto',
    credit_card: t('Cartão')
  };
  return labels[method || ''] || method || '';
};

const handleOpenLink = (): void => {
  if (!props.paymentLink?.url) return;
  window.open(props.paymentLink.url, '_blank');
  emit('open-link', props.paymentLink);
};

const handleCopyLink = async (): Promise<void> => {
  if (!props.paymentLink?.url) return;

  log('PaymentLinksCard', 'handleCopyLink', 'Copying link');

  try {
    await navigator.clipboard.writeText(props.paymentLink.url);
    eventBus.emit('globalMessage.success', t('Link copiado!'));
    emit('copy', props.paymentLink);
  } catch (error) {
    log('PaymentLinksCard', 'handleCopyLink', 'Error', error);
  }
};

const handleSendWhatsApp = (): void => {
  log('PaymentLinksCard', 'handleSendWhatsApp', 'Send WhatsApp requested');
  emit('send-whatsapp', props.paymentLink);
};

const handleViewDetails = (): void => {
  log('PaymentLinksCard', 'handleViewDetails', 'View details', props.paymentLink.id);
  eventBus.emit('modal.payment-link.open', { id: props.paymentLink.id });
  emit('view-details', props.paymentLink);
};
</script>

<style scoped>
.payment-link-card {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  background-color: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  transition: box-shadow 0.2s ease;
}

.payment-link-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.card-active {
  border-left: 3px solid var(--blue-500);
}

.card-paid {
  border-left: 3px solid var(--green-500);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.card-date {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.card-body {
  flex: 1;
}

.card-amount {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-color);
  margin-bottom: 0.5rem;
}

.card-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-row {
  display: flex;
  gap: 0.5rem;
  font-size: 0.8rem;
}

.detail-label {
  color: var(--text-color-secondary);
}

.detail-value {
  color: var(--text-color);
  font-weight: 500;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.25rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--surface-border);
}
</style>
